import {
  differenceInCalendarDays,
  endOfMonth,
  format,
  isWithinInterval,
  startOfMonth,
  startOfWeek,
  subDays,
  subMonths,
} from "date-fns";
import { orderDealValueSegmentRows } from "../lib/dealValueSegments";

export type GrowthEfficiencyDimension =
  | "market"
  | "pipeline"
  | "product"
  | "region"
  | "sales_rep"
  | "segment"
  | "source"
  | "team";

export type GrowthEfficiencyPeriodRange = {
  from: Date;
  to: Date;
};

export type GrowthEfficiencyPhase = "early" | "middle" | "late";

export type GrowthEfficiencyPhaseActivity = Record<
  GrowthEfficiencyPhase,
  {
    activeWeeks?: number;
    meetingHours?: number;
    meetings: number;
    sellerEmails: number;
  }
>;

export type GrowthEfficiencyMeetingActivity = {
  durationHours: number;
  sellerParticipantCount?: number | null;
  timestamp: Date;
};

export type GrowthEfficiencyStageHistoryRecord = {
  stageId: string;
  timestamp: Date;
};

export type GrowthEfficiencyDealInput = {
  closedAt: Date | null;
  createdAt: Date | null;
  dimensions: Partial<Record<GrowthEfficiencyDimension, string>>;
  id: number | string;
  isLost: boolean;
  isWon: boolean;
  name?: string;
  meetingActivities?: GrowthEfficiencyMeetingActivity[] | null;
  meetingTimestamps?: Date[] | null;
  meetings: number | null;
  phaseActivity?: GrowthEfficiencyPhaseActivity | null;
  pipelineActivityTimestamps?: Date[] | null;
  pipelineId: string;
  sellerEmails: number | null;
  sellerEmailTimestamps?: Date[] | null;
  stageHistory?: GrowthEfficiencyStageHistoryRecord[] | null;
  value: number;
};

export type GrowthEfficiencyModelConfig = {
  activeWeekWeight?: number;
  baseDayWeight: number;
  companyShrinkageStrength: number;
  cycleAdjustmentExponent: number;
  emailWeight: number;
  meetingWeight: number;
  meetingPreparationHours: number;
  minimumCoverage: number;
  minimumDeals: number;
  minimumGroupDeals: number;
  pipelineShrinkageStrength: number;
  referenceCycleDays: number;
  useActivePipelineWeeks: boolean;
  useActivityCaps: boolean;
  useMeetingDuration: boolean;
  version: "v1" | "v2";
};

export const growthEfficiencyModelConfig: GrowthEfficiencyModelConfig = {
  baseDayWeight: 0.15,
  companyShrinkageStrength: 20,
  cycleAdjustmentExponent: 0.5,
  emailWeight: 0.1,
  meetingWeight: 1,
  meetingPreparationHours: 0,
  minimumCoverage: 0.6,
  minimumDeals: 5,
  minimumGroupDeals: 3,
  pipelineShrinkageStrength: 30,
  referenceCycleDays: 90,
  useActivePipelineWeeks: false,
  useActivityCaps: true,
  useMeetingDuration: false,
  version: "v1",
};

type GrowthEfficiencyCaps = {
  cycleDays: number | null;
  meetings: number | null;
  sellerEmails: number | null;
};

export type GrowthEfficiencyCalibration = {
  caps: GrowthEfficiencyCaps;
  companyCycleDays: number;
  companyCycleSampleSize: number;
  configVersion: GrowthEfficiencyModelConfig["version"];
  pipelineCycleDays: Record<string, number>;
  referenceCycleDays: number;
};

export type GrowthEfficiencyDealEffort = GrowthEfficiencyDealInput & {
  activePipelineWeeks: number;
  cycleDays: number;
  dayWeight: number;
  emailEffort: number;
  meetingEffort: number;
  timeEffort: number;
  totalEffort: number;
};

export type GrowthEfficiencyCoverage = {
  complete: number;
  cycle: number;
  email: number;
  meeting: number;
  population: number;
};

export type GrowthEfficiencyPhaseEffortRow = {
  label: string;
  lostEmail: number | null;
  lostMeeting: number | null;
  lostPipelineTime: number | null;
  phase: GrowthEfficiencyPhase;
  wonEmail: number | null;
  wonMeeting: number | null;
  wonPipelineTime: number | null;
};

export type GrowthEfficiencyPosition = {
  averageWonDealValue: number | null;
  closedDeals: number;
  coverage: GrowthEfficiencyCoverage;
  eligibleDeals: number;
  emailEffort: number;
  emailEffortPerDeal: number | null;
  index: number | null;
  lostDeals: number;
  lostEffort: number;
  lostEffortShare: number | null;
  lostEffortPerDeal: number | null;
  lostEmailEffort: number;
  lostMeetingEffort: number;
  lostTimeEffort: number;
  medianCycleDays: number | null;
  meetingEffort: number;
  meetingEffortPerDeal: number | null;
  phaseEffort: GrowthEfficiencyPhaseEffortRow[];
  timeEffort: number;
  timeEffortPerDeal: number | null;
  totalEffort: number;
  totalEffortPerDeal: number | null;
  winRate: number | null;
  wonDeals: number;
  wonEffort: number;
  wonEffortPerDeal: number | null;
  wonEmailEffort: number;
  wonMeetingEffort: number;
  wonTimeEffort: number;
  wonValue: number;
  yield: number | null;
};

export type GrowthEfficiencyDriver = {
  change: number | null;
  current: number | null;
  impact: "helping" | "hurting" | "neutral" | "unavailable";
  key:
    | "average_won_deal_value"
    | "email_effort"
    | "meeting_effort"
    | "pipeline_time"
    | "win_rate";
  label: string;
  previous: number | null;
  unit: "currency" | "days" | "effort" | "percentage";
};

export type GrowthEfficiencyBreakdownRow = {
  change: number | null;
  closedDeals: number;
  coverage: number;
  eligibleDeals: number;
  index: number | null;
  key: string;
  label: string;
  lostEffortShare: number | null;
  emailHoursPerDeal: number | null;
  meetingHoursPerDeal: number | null;
  medianCycleDays: number | null;
  revenuePerSalesHour: number | null;
  salesHoursPerDeal: number | null;
  dealOverheadHoursPerDeal: number | null;
  winRate: number | null;
};

export type GrowthEfficiencyTrendRow = {
  coverage: number | null;
  eligibleDeals: number;
  index: number | null;
  label: string;
  lostDeals: number;
  month: string;
  populationDeals: number;
  wonDeals: number;
};

export type GrowthEfficiencyRecentTrendRow = {
  coverage: number | null;
  eligibleDeals: number;
  label: string;
  lostDeals: number;
  month: string;
  populationDeals: number;
  revenuePerSalesHour: number | null;
  wonDeals: number;
};

export type GrowthEfficiencyAnalysis = {
  breakdown: GrowthEfficiencyBreakdownRow[];
  calibration: GrowthEfficiencyCalibration;
  comparison: GrowthEfficiencyPosition;
  current: GrowthEfficiencyPosition;
  drivers: GrowthEfficiencyDriver[];
  indexChange: number | null;
  recentTrend: GrowthEfficiencyRecentTrendRow[];
  reference: GrowthEfficiencyPosition;
  trend: GrowthEfficiencyTrendRow[];
};

type BuildGrowthEfficiencyAnalysisInput = {
  breakdownDimension: GrowthEfficiencyDimension;
  comparisonRange: GrowthEfficiencyPeriodRange;
  config?: GrowthEfficiencyModelConfig;
  currentRange: GrowthEfficiencyPeriodRange;
  deals: GrowthEfficiencyDealInput[];
  referenceDeals?: GrowthEfficiencyDealInput[];
  referenceRange: GrowthEfficiencyPeriodRange;
};

function finiteNonNegative(value: number | null | undefined) {
  return value !== null
    && value !== undefined
    && Number.isFinite(value)
    && value >= 0;
}

function median(values: number[]) {
  if (!values.length) return null;
  const sorted = values.toSorted((a, b) => a - b);
  const middle = Math.floor(sorted.length / 2);
  return sorted.length % 2
    ? sorted[middle]
    : (sorted[middle - 1] + sorted[middle]) / 2;
}

function quantile(values: number[], percentile: number) {
  if (!values.length) return null;
  const sorted = values.toSorted((a, b) => a - b);
  const index = (sorted.length - 1) * percentile;
  const lower = Math.floor(index);
  const upper = Math.ceil(index);
  if (lower === upper) return sorted[lower];
  return sorted[lower] + (sorted[upper] - sorted[lower]) * (index - lower);
}

function getCycleDays(deal: GrowthEfficiencyDealInput) {
  if (!deal.createdAt || !deal.closedAt) return null;
  const cycleDays = differenceInCalendarDays(deal.closedAt, deal.createdAt);
  return Number.isFinite(cycleDays) && cycleDays >= 0 ? cycleDays : null;
}

function isClosedOutcomeDeal(deal: GrowthEfficiencyDealInput) {
  return Boolean(
    deal.closedAt
    && deal.isWon !== deal.isLost
    && Number.isFinite(deal.value)
    && deal.value > 0,
  );
}

function dealsInRange(
  deals: GrowthEfficiencyDealInput[],
  range: GrowthEfficiencyPeriodRange,
) {
  return Array.from(
    new Map(
      deals
        .filter(deal => (
          isClosedOutcomeDeal(deal)
          && deal.closedAt
          && isWithinInterval(deal.closedAt, {
            start: range.from,
            end: range.to,
          })
        ))
        .map(deal => [String(deal.id), deal]),
    ).values(),
  );
}

// Shared population contract for Sales Economics readiness and the calculation.
export { dealsInRange as getGrowthEfficiencyDealsInRange };

function shrunkValue(
  localValue: number,
  fallback: number,
  sampleSize: number,
  strength: number,
) {
  const localWeight = sampleSize / (sampleSize + strength);
  return localWeight * localValue + (1 - localWeight) * fallback;
}

export function getGrowthEfficiencyDayWeight(
  typicalCycleDays: number,
  config: GrowthEfficiencyModelConfig = growthEfficiencyModelConfig,
) {
  const safeCycleDays = Math.max(1, typicalCycleDays);
  return config.baseDayWeight * Math.pow(
    config.referenceCycleDays / safeCycleDays,
    config.cycleAdjustmentExponent,
  );
}

export function buildGrowthEfficiencyCalibration(
  referenceDeals: GrowthEfficiencyDealInput[],
  config: GrowthEfficiencyModelConfig = growthEfficiencyModelConfig,
): GrowthEfficiencyCalibration {
  const cycleDeals = referenceDeals.flatMap((deal) => {
    const cycleDays = getCycleDays(deal);
    return cycleDays === null ? [] : [{ cycleDays, pipelineId: deal.pipelineId }];
  });
  const rawCompanyMedian = median(cycleDeals.map(deal => deal.cycleDays))
    ?? config.referenceCycleDays;
  const companyCycleDays = shrunkValue(
    rawCompanyMedian,
    config.referenceCycleDays,
    cycleDeals.length,
    config.companyShrinkageStrength,
  );
  const pipelineIds = new Set(cycleDeals.map(deal => deal.pipelineId));
  const pipelineCycleDays = Object.fromEntries(
    Array.from(pipelineIds).map((pipelineId) => {
      const pipelineCycles = cycleDeals
        .filter(deal => deal.pipelineId === pipelineId)
        .map(deal => deal.cycleDays);
      const pipelineMedian = median(pipelineCycles) ?? companyCycleDays;
      return [
        pipelineId,
        shrunkValue(
          pipelineMedian,
          companyCycleDays,
          pipelineCycles.length,
          config.pipelineShrinkageStrength,
        ),
      ];
    }),
  );
  const meetings = referenceDeals
    .map(deal => deal.meetings)
    .filter(finiteNonNegative) as number[];
  const sellerEmails = referenceDeals
    .map(deal => deal.sellerEmails)
    .filter(finiteNonNegative) as number[];

  return {
    caps: {
      cycleDays: config.useActivityCaps
        ? quantile(cycleDeals.map(deal => deal.cycleDays), 0.99)
        : null,
      meetings: config.useActivityCaps ? quantile(meetings, 0.95) : null,
      sellerEmails: config.useActivityCaps ? quantile(sellerEmails, 0.95) : null,
    },
    companyCycleDays,
    companyCycleSampleSize: cycleDeals.length,
    configVersion: config.version,
    pipelineCycleDays,
    referenceCycleDays: config.referenceCycleDays,
  };
}

function capValue(value: number, cap: number | null) {
  return cap === null ? value : Math.min(value, cap);
}

function uniqueWeekCount(timestamps: Date[]) {
  return new Set(timestamps.map(timestamp => (
    startOfWeek(timestamp, { weekStartsOn: 1 }).toISOString()
  ))).size;
}

export function buildGrowthEfficiencyDealEffort(
  deal: GrowthEfficiencyDealInput,
  calibration: GrowthEfficiencyCalibration,
  config: GrowthEfficiencyModelConfig = growthEfficiencyModelConfig,
): GrowthEfficiencyDealEffort | null {
  const rawCycleDays = getCycleDays(deal);
  if (
    rawCycleDays === null
    || !finiteNonNegative(deal.meetings)
    || !finiteNonNegative(deal.sellerEmails)
    || (config.useMeetingDuration && !Array.isArray(deal.meetingActivities))
    || (config.useActivePipelineWeeks
      && !Array.isArray(deal.pipelineActivityTimestamps))
  ) {
    return null;
  }

  const cycleDays = capValue(rawCycleDays, calibration.caps.cycleDays);
  const meetings = capValue(Number(deal.meetings), calibration.caps.meetings);
  const sellerEmails = capValue(
    Number(deal.sellerEmails),
    calibration.caps.sellerEmails,
  );
  const typicalCycleDays = calibration.pipelineCycleDays[deal.pipelineId]
    ?? calibration.companyCycleDays;
  const dayWeight = getGrowthEfficiencyDayWeight(typicalCycleDays, config);
  const meetingEffort = config.useMeetingDuration
    ? (deal.meetingActivities ?? []).reduce(
        (sum, meeting) => sum
          + meeting.durationHours * (meeting.sellerParticipantCount ?? 1)
          + config.meetingPreparationHours,
        0,
      )
    : meetings * config.meetingWeight;
  const emailEffort = sellerEmails * config.emailWeight;
  const activePipelineWeeks = config.useActivePipelineWeeks
    ? uniqueWeekCount(deal.pipelineActivityTimestamps ?? [])
    : 0;
  const timeEffort = config.useActivePipelineWeeks
    ? activePipelineWeeks * (config.activeWeekWeight ?? dayWeight * 7)
    : cycleDays * dayWeight;

  return {
    ...deal,
    activePipelineWeeks,
    cycleDays: rawCycleDays,
    dayWeight,
    emailEffort,
    meetingEffort,
    timeEffort,
    totalEffort: meetingEffort + emailEffort + timeEffort,
  };
}

function buildCoverage(
  deals: GrowthEfficiencyDealInput[],
  config: GrowthEfficiencyModelConfig,
) {
  const population = deals.length;
  const cycle = deals.filter(deal => getCycleDays(deal) !== null).length;
  const meeting = deals.filter(deal => finiteNonNegative(deal.meetings)).length;
  const email = deals.filter(deal => finiteNonNegative(deal.sellerEmails)).length;
  const complete = deals.filter(deal => (
    getCycleDays(deal) !== null
    && finiteNonNegative(deal.meetings)
    && finiteNonNegative(deal.sellerEmails)
    && (!config.useMeetingDuration || Array.isArray(deal.meetingActivities))
    && (!config.useActivePipelineWeeks
      || Array.isArray(deal.pipelineActivityTimestamps))
  )).length;

  return { complete, cycle, email, meeting, population };
}

function safeAverage(value: number, count: number) {
  return count > 0 ? value / count : null;
}

const growthEfficiencyPhases: Array<{
  label: string;
  phase: GrowthEfficiencyPhase;
}> = [
  { label: "First third", phase: "early" },
  { label: "Middle third", phase: "middle" },
  { label: "Final third", phase: "late" },
];

function buildPhaseEffort(efforts: GrowthEfficiencyDealEffort[]) {
  const phaseEfforts = efforts.filter(
    (deal): deal is GrowthEfficiencyDealEffort & {
      phaseActivity: GrowthEfficiencyPhaseActivity;
    } => Boolean(deal.phaseActivity),
  );
  const wonCount = phaseEfforts.filter(deal => deal.isWon).length;
  const lostCount = phaseEfforts.filter(deal => deal.isLost).length;

  return growthEfficiencyPhases.map(({ label, phase }) => {
    const totals = phaseEfforts.reduce((result, deal) => {
      const activity = deal.phaseActivity;
      const totalMeetings = Object.values(activity)
        .reduce((sum, row) => sum + row.meetings, 0);
      const totalMeetingHours = Object.values(activity)
        .reduce((sum, row) => sum + (row.meetingHours ?? 0), 0);
      const totalEmails = Object.values(activity)
        .reduce((sum, row) => sum + row.sellerEmails, 0);
      const totalActiveWeeks = Object.values(activity)
        .reduce((sum, row) => sum + (row.activeWeeks ?? 0), 0);
      const prefix = deal.isWon ? "won" : "lost";
      const meetingBasis = totalMeetingHours > 0 ? totalMeetingHours : totalMeetings;
      const phaseMeetingBasis = totalMeetingHours > 0
        ? (activity[phase].meetingHours ?? 0)
        : activity[phase].meetings;
      result[`${prefix}Meeting`] += meetingBasis > 0
        ? deal.meetingEffort * phaseMeetingBasis / meetingBasis
        : 0;
      result[`${prefix}Email`] += totalEmails > 0
        ? deal.emailEffort * activity[phase].sellerEmails / totalEmails
        : 0;
      result[`${prefix}PipelineTime`] += totalActiveWeeks > 0
        ? deal.timeEffort * (activity[phase].activeWeeks ?? 0) / totalActiveWeeks
        : deal.timeEffort / 3;
      return result;
    }, {
      lostEmail: 0,
      lostMeeting: 0,
      lostPipelineTime: 0,
      wonEmail: 0,
      wonMeeting: 0,
      wonPipelineTime: 0,
    });

    return {
      label,
      lostEmail: safeAverage(totals.lostEmail, lostCount),
      lostMeeting: safeAverage(totals.lostMeeting, lostCount),
      lostPipelineTime: safeAverage(totals.lostPipelineTime, lostCount),
      phase,
      wonEmail: safeAverage(totals.wonEmail, wonCount),
      wonMeeting: safeAverage(totals.wonMeeting, wonCount),
      wonPipelineTime: safeAverage(totals.wonPipelineTime, wonCount),
    } satisfies GrowthEfficiencyPhaseEffortRow;
  });
}

function buildPosition(
  deals: GrowthEfficiencyDealInput[],
  calibration: GrowthEfficiencyCalibration,
  referenceYield: number | null,
  minimumDeals: number,
  config: GrowthEfficiencyModelConfig,
): GrowthEfficiencyPosition {
  const coverage = buildCoverage(deals, config);
  const efforts = deals.flatMap((deal) => {
    const effort = buildGrowthEfficiencyDealEffort(deal, calibration, config);
    return effort ? [effort] : [];
  });
  const wonDeals = efforts.filter(deal => deal.isWon);
  const lostDeals = efforts.filter(deal => deal.isLost);
  const meetingEffort = efforts.reduce((sum, deal) => sum + deal.meetingEffort, 0);
  const emailEffort = efforts.reduce((sum, deal) => sum + deal.emailEffort, 0);
  const timeEffort = efforts.reduce((sum, deal) => sum + deal.timeEffort, 0);
  const totalEffort = meetingEffort + emailEffort + timeEffort;
  const lostEffort = lostDeals.reduce((sum, deal) => sum + deal.totalEffort, 0);
  const wonEffort = wonDeals.reduce((sum, deal) => sum + deal.totalEffort, 0);
  const lostEmailEffort = lostDeals.reduce((sum, deal) => sum + deal.emailEffort, 0);
  const lostMeetingEffort = lostDeals.reduce((sum, deal) => sum + deal.meetingEffort, 0);
  const lostTimeEffort = lostDeals.reduce((sum, deal) => sum + deal.timeEffort, 0);
  const wonEmailEffort = wonDeals.reduce((sum, deal) => sum + deal.emailEffort, 0);
  const wonMeetingEffort = wonDeals.reduce((sum, deal) => sum + deal.meetingEffort, 0);
  const wonTimeEffort = wonDeals.reduce((sum, deal) => sum + deal.timeEffort, 0);
  const wonValue = wonDeals.reduce((sum, deal) => sum + deal.value, 0);
  const yieldValue = totalEffort > 0 ? wonValue / totalEffort : null;
  const completeCoverage = coverage.population > 0
    ? coverage.complete / coverage.population
    : 0;
  const meetsQualityBar = efforts.length >= minimumDeals
    && completeCoverage >= config.minimumCoverage;
  const index = meetsQualityBar
    && yieldValue !== null
    && referenceYield !== null
    && referenceYield > 0
    ? 100 * yieldValue / referenceYield
    : null;

  return {
    averageWonDealValue: safeAverage(wonValue, wonDeals.length),
    closedDeals: deals.length,
    coverage,
    eligibleDeals: efforts.length,
    emailEffort,
    emailEffortPerDeal: safeAverage(emailEffort, efforts.length),
    index,
    lostDeals: lostDeals.length,
    lostEffort,
    lostEffortShare: totalEffort > 0 ? lostEffort / totalEffort : null,
    lostEffortPerDeal: safeAverage(lostEffort, lostDeals.length),
    lostEmailEffort,
    lostMeetingEffort,
    lostTimeEffort,
    medianCycleDays: median(efforts.map(deal => deal.cycleDays)),
    meetingEffort,
    meetingEffortPerDeal: safeAverage(meetingEffort, efforts.length),
    phaseEffort: buildPhaseEffort(efforts),
    timeEffort,
    timeEffortPerDeal: safeAverage(timeEffort, efforts.length),
    totalEffort,
    totalEffortPerDeal: safeAverage(totalEffort, efforts.length),
    winRate: efforts.length > 0 ? wonDeals.length / efforts.length : null,
    wonDeals: wonDeals.length,
    wonEffort,
    wonEffortPerDeal: safeAverage(wonEffort, wonDeals.length),
    wonEmailEffort,
    wonMeetingEffort,
    wonTimeEffort,
    wonValue,
    yield: meetsQualityBar ? yieldValue : null,
  };
}

function numericChange(current: number | null, previous: number | null) {
  return current !== null && previous !== null ? current - previous : null;
}

function relativeChange(current: number | null, previous: number | null) {
  if (current === null || previous === null || previous === 0) return null;
  return current / previous - 1;
}

function driverImpact(
  change: number | null,
  lowerIsBetter: boolean,
): GrowthEfficiencyDriver["impact"] {
  if (change === null) return "unavailable";
  if (Math.abs(change) < 0.005) return "neutral";
  const improves = lowerIsBetter ? change < 0 : change > 0;
  return improves ? "helping" : "hurting";
}

function buildDrivers(
  current: GrowthEfficiencyPosition,
  comparison: GrowthEfficiencyPosition,
): GrowthEfficiencyDriver[] {
  const rows: Array<Omit<GrowthEfficiencyDriver, "change" | "impact"> & {
    change: number | null;
    lowerIsBetter: boolean;
  }> = [
    {
      change: numericChange(current.winRate, comparison.winRate),
      current: current.winRate,
      key: "win_rate",
      label: "Win rate",
      lowerIsBetter: false,
      previous: comparison.winRate,
      unit: "percentage",
    },
    {
      change: relativeChange(
        current.averageWonDealValue,
        comparison.averageWonDealValue,
      ),
      current: current.averageWonDealValue,
      key: "average_won_deal_value",
      label: "Average won Deal value",
      lowerIsBetter: false,
      previous: comparison.averageWonDealValue,
      unit: "currency",
    },
    {
      change: relativeChange(
        current.meetingEffortPerDeal,
        comparison.meetingEffortPerDeal,
      ),
      current: current.meetingEffortPerDeal,
      key: "meeting_effort",
      label: "Meeting effort",
      lowerIsBetter: true,
      previous: comparison.meetingEffortPerDeal,
      unit: "effort",
    },
    {
      change: relativeChange(
        current.emailEffortPerDeal,
        comparison.emailEffortPerDeal,
      ),
      current: current.emailEffortPerDeal,
      key: "email_effort",
      label: "Email effort",
      lowerIsBetter: true,
      previous: comparison.emailEffortPerDeal,
      unit: "effort",
    },
    {
      change: relativeChange(
        current.timeEffortPerDeal,
        comparison.timeEffortPerDeal,
      ),
      current: current.medianCycleDays,
      key: "pipeline_time",
      label: "Time in pipeline",
      lowerIsBetter: true,
      previous: comparison.medianCycleDays,
      unit: "days",
    },
  ];

  return rows.map(({ lowerIsBetter, ...row }) => ({
    ...row,
    impact: driverImpact(row.change, lowerIsBetter),
  }));
}

function groupDeals(
  deals: GrowthEfficiencyDealInput[],
  dimension: GrowthEfficiencyDimension,
) {
  const groups = new Map<string, GrowthEfficiencyDealInput[]>();
  for (const deal of deals) {
    const label = deal.dimensions[dimension]?.trim() || "Not specified";
    groups.set(label, [...(groups.get(label) ?? []), deal]);
  }
  return groups;
}

function buildBreakdown(
  currentDeals: GrowthEfficiencyDealInput[],
  comparisonDeals: GrowthEfficiencyDealInput[],
  dimension: GrowthEfficiencyDimension,
  calibration: GrowthEfficiencyCalibration,
  referenceYield: number | null,
  config: GrowthEfficiencyModelConfig,
) {
  const currentGroups = groupDeals(currentDeals, dimension);
  const comparisonGroups = groupDeals(comparisonDeals, dimension);
  const rows = Array.from(currentGroups, ([label, deals]) => {
    const current = buildPosition(
      deals,
      calibration,
      referenceYield,
      config.minimumGroupDeals,
      config,
    );
    const comparison = buildPosition(
      comparisonGroups.get(label) ?? [],
      calibration,
      referenceYield,
      config.minimumGroupDeals,
      config,
    );
    return {
      change: numericChange(current.index, comparison.index),
      closedDeals: current.closedDeals,
      coverage: current.coverage.population > 0
        ? current.coverage.complete / current.coverage.population
        : 0,
      eligibleDeals: current.eligibleDeals,
      emailHoursPerDeal: current.emailEffortPerDeal,
      index: current.index,
      key: label.toLowerCase().replaceAll(/[^a-z0-9]+/g, "-") || "not-specified",
      label,
      lostEffortShare: current.lostEffortShare,
      meetingHoursPerDeal: current.meetingEffortPerDeal,
      medianCycleDays: current.medianCycleDays,
      revenuePerSalesHour: current.yield,
      salesHoursPerDeal: current.totalEffortPerDeal,
      dealOverheadHoursPerDeal: current.timeEffortPerDeal,
      winRate: current.winRate,
    } satisfies GrowthEfficiencyBreakdownRow;
  });

  if (dimension === "segment") return orderDealValueSegmentRows(rows);

  return rows.toSorted((a, b) => {
    const aCatchAll = a.label === "Not specified";
    const bCatchAll = b.label === "Not specified";
    if (aCatchAll !== bCatchAll) return aCatchAll ? 1 : -1;
    if (a.index === null && b.index !== null) return 1;
    if (a.index !== null && b.index === null) return -1;
    return (b.index ?? 0) - (a.index ?? 0) || a.label.localeCompare(b.label);
  });
}

export function getGrowthEfficiencyTrendRange(monthEnd: Date): GrowthEfficiencyPeriodRange {
  return { from: startOfMonth(subMonths(monthEnd, 11)), to: monthEnd };
}

export function getGrowthEfficiencyRecentTrendRange(monthEnd: Date): GrowthEfficiencyPeriodRange {
  return { from: subDays(monthEnd, 89), to: monthEnd };
}

function buildTrend(
  deals: GrowthEfficiencyDealInput[],
  selectedEnd: Date,
  calibration: GrowthEfficiencyCalibration,
  referenceYield: number | null,
  config: GrowthEfficiencyModelConfig,
) {
  const currentMonthStart = startOfMonth(new Date());
  const anchorMonthEnd = selectedEnd >= currentMonthStart
    ? endOfMonth(subMonths(currentMonthStart, 1))
    : endOfMonth(selectedEnd);
  return Array.from({ length: 12 }, (_, index) => {
    const monthEnd = endOfMonth(subMonths(anchorMonthEnd, 11 - index));
    const position = buildPosition(
      dealsInRange(deals, getGrowthEfficiencyTrendRange(monthEnd)),
      calibration,
      referenceYield,
      config.minimumDeals,
      config,
    );
    return {
      coverage: position.coverage.population > 0
        ? position.coverage.complete / position.coverage.population
        : null,
      eligibleDeals: position.eligibleDeals,
      index: position.index,
      label: format(monthEnd, "MMM"),
      lostDeals: position.lostDeals,
      month: format(monthEnd, "yyyy-MM"),
      populationDeals: position.coverage.population,
      wonDeals: position.wonDeals,
    } satisfies GrowthEfficiencyTrendRow;
  });
}

function buildRecentTrend(
  deals: GrowthEfficiencyDealInput[],
  selectedEnd: Date,
  calibration: GrowthEfficiencyCalibration,
  referenceYield: number | null,
  config: GrowthEfficiencyModelConfig,
) {
  const currentMonthStart = startOfMonth(new Date());
  const anchorMonthEnd = selectedEnd >= currentMonthStart
    ? endOfMonth(subMonths(currentMonthStart, 1))
    : endOfMonth(selectedEnd);
  return Array.from({ length: 6 }, (_, index) => {
    const monthEnd = endOfMonth(subMonths(anchorMonthEnd, 5 - index));
    const position = buildPosition(
      dealsInRange(deals, getGrowthEfficiencyRecentTrendRange(monthEnd)),
      calibration,
      referenceYield,
      config.minimumDeals,
      config,
    );
    return {
      coverage: position.coverage.population > 0
        ? position.coverage.complete / position.coverage.population
        : null,
      eligibleDeals: position.eligibleDeals,
      label: format(monthEnd, "MMM"),
      lostDeals: position.lostDeals,
      month: format(monthEnd, "yyyy-MM"),
      populationDeals: position.coverage.population,
      revenuePerSalesHour: position.yield,
      wonDeals: position.wonDeals,
    } satisfies GrowthEfficiencyRecentTrendRow;
  });
}

export function buildGrowthEfficiencyAnalysis({
  breakdownDimension,
  comparisonRange,
  config = growthEfficiencyModelConfig,
  currentRange,
  deals,
  referenceDeals: referenceInputDeals,
  referenceRange,
}: BuildGrowthEfficiencyAnalysisInput): GrowthEfficiencyAnalysis {
  const referenceDeals = dealsInRange(referenceInputDeals ?? deals, referenceRange);
  const calibration = buildGrowthEfficiencyCalibration(referenceDeals, config);
  const referenceWithoutIndex = buildPosition(
    referenceDeals,
    calibration,
    null,
    config.minimumDeals,
    config,
  );
  const referenceYield = referenceWithoutIndex.yield;
  const reference = buildPosition(
    referenceDeals,
    calibration,
    referenceYield,
    config.minimumDeals,
    config,
  );
  const currentDeals = dealsInRange(deals, currentRange);
  const comparisonDeals = dealsInRange(deals, comparisonRange);
  const current = buildPosition(
    currentDeals,
    calibration,
    referenceYield,
    config.minimumDeals,
    config,
  );
  const comparison = buildPosition(
    comparisonDeals,
    calibration,
    referenceYield,
    config.minimumDeals,
    config,
  );

  return {
    breakdown: buildBreakdown(
      currentDeals,
      comparisonDeals,
      breakdownDimension,
      calibration,
      referenceYield,
      config,
    ),
    calibration,
    comparison,
    current,
    drivers: buildDrivers(current, comparison),
    indexChange: numericChange(current.index, comparison.index),
    recentTrend: buildRecentTrend(
      deals,
      currentRange.to,
      calibration,
      referenceYield,
      config,
    ),
    reference,
    trend: buildTrend(
      deals,
      currentRange.to,
      calibration,
      referenceYield,
      config,
    ),
  };
}
